import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  createTemplate,
  uploadTemplateMedia,
  getWhatsAppErrorMessage,
} from "../../services/whatsappApi";
import TemplatePhonePreview from "../../components/whatsapp/templates/TemplatePhonePreview";
import TemplateCatalogStep from "../../components/whatsapp/templates/TemplateCatalogStep";
import TemplateCarouselStep from "../../components/whatsapp/templates/TemplateCarouselStep";
import {
  getWizardSteps,
  TEMPLATE_TYPES,
  TEMPLATE_LANGUAGES,
  TEMPLATE_CATEGORIES,
  HEADER_MEDIA_TYPES,
  getHeaderMediaTypeMeta,
  validateHeaderMediaSample,
  EMPTY_TEMPLATE_FORM,
  createEmptyCarouselCard,
  normalizeTemplateName,
  extractTemplateVariables,
  insertVariableAtCursor,
  nextVariableIndex,
  buildTemplateComponents,
  validateWizardStep,
} from "../../utils/whatsappTemplateBuilder";
import "./whatsapp.css";
import WhatsAppPageShell from "./WhatsAppPageShell";

function extractMediaHandle(res) {
  return (
    res?.handle ??
    res?.h ??
    res?.data?.handle ??
    res?.data?.h ??
    res?.data?.id ??
    res?.id ??
    null
  );
}

function InfoBox({ children }) {
  return (
    <div className="wa-tpl-info-box">
      <Icon icon="solar:info-circle-linear" width={18} />
      <ul>{children}</ul>
    </div>
  );
}

export default function WhatsAppTemplateCreate() {
  const navigate = useNavigate();
  const bodyRef = useRef(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_TEMPLATE_FORM);
  const [stepError, setStepError] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const wizardSteps = useMemo(
    () => getWizardSteps(form.templateType),
    [form.templateType],
  );
  const currentStepKey = wizardSteps[step]?.key || "basic";
  const isReviewStep = currentStepKey === "review";

  useEffect(() => {
    if (step >= wizardSteps.length) {
      setStep(Math.max(0, wizardSteps.length - 1));
    }
  }, [wizardSteps.length, step]);

  const variables = useMemo(
    () => extractTemplateVariables(form.bodyText, form.headerText),
    [form.bodyText, form.headerText],
  );

  const updateForm = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const goNext = () => {
    const errors = validateWizardStep(currentStepKey, form);
    if (errors.length) {
      setStepError(errors[0]);
      return;
    }
    setStepError("");
    setStep((s) => Math.min(s + 1, wizardSteps.length - 1));
  };

  const goBack = () => {
    setStepError("");
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleAddVariable = () => {
    const el = bodyRef.current;
    const cursor = el?.selectionStart ?? form.bodyText.length;
    const num = nextVariableIndex(form.bodyText, form.headerText);
    const { text, cursor: nextCursor } = insertVariableAtCursor(
      form.bodyText,
      cursor,
      num,
    );
    updateForm({ bodyText: text });
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(nextCursor, nextCursor);
      }
    });
  };

  const handleAddQuickReply = () => {
    if (form.quickReplyButtons.length >= 3) return;
    updateForm({
      quickReplyButtons: [...form.quickReplyButtons, { text: "" }],
    });
  };

  const handleAddCta = (type = "PHONE") => {
    if (form.ctaButtons.length >= 2) return;
    updateForm({
      ctaButtons: [
        ...form.ctaButtons,
        { type, text: "", phone: "", url: "" },
      ],
    });
  };

  const handleSubmit = async () => {
    const allErrors = [];
    wizardSteps
      .filter((s) => s.key !== "review")
      .forEach((s) => {
        allErrors.push(...validateWizardStep(s.key, form));
      });
    if (allErrors.length) {
      setStepError(allErrors[0]);
      return;
    }

    setSubmitting(true);
    setStepError("");
    try {
      const mediaHandles = {};

      if (form.templateType === "STANDARD" && form.headerFormat === "MEDIA" && form.headerMediaFile) {
        const mediaRes = await uploadTemplateMedia(form.headerMediaFile);
        mediaHandles.header = extractMediaHandle(mediaRes);
        if (!mediaHandles.header) {
          throw new Error("Media upload did not return a handle");
        }
      }

      if (form.templateType === "CATALOG" && form.catalogThumbnailFile) {
        const mediaRes = await uploadTemplateMedia(form.catalogThumbnailFile);
        mediaHandles.catalogThumbnail = extractMediaHandle(mediaRes);
      }

      if (form.templateType === "CAROUSEL") {
        const handles = [];
        for (const card of form.carouselCards || []) {
          if (!card.imageFile) {
            handles.push(null);
            continue;
          }
          const mediaRes = await uploadTemplateMedia(card.imageFile);
          handles.push(extractMediaHandle(mediaRes));
        }
        mediaHandles.carouselCards = handles;
      }

      const payload = {
        name: normalizeTemplateName(form.name),
        language: form.language || "en",
        category:
          form.templateType === "CATALOG" ? "MARKETING" : form.category,
        components: buildTemplateComponents(form, mediaHandles),
      };

      const res = await createTemplate(payload);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to create template");
      }

      toast.success(
        res?.status
          ? `Template submitted (${res.status})`
          : "Template submitted for approval",
      );
      setForm(EMPTY_TEMPLATE_FORM);
      setStep(0);
      setStepError("");
    } catch (err) {
      const msg = getWhatsAppErrorMessage(err, "Failed to create template");
      setStepError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    if (currentStepKey === "basic") {
      return (
        <>
          <div className="wa-tpl-type-grid">
            {TEMPLATE_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                className={`wa-tpl-type-card active-${type.accent} ${form.templateType === type.value ? "active" : ""}`}
                onClick={() => {
                  updateForm({
                    templateType: type.value,
                    ...(type.value === "CAROUSEL" && !(form.carouselCards || []).length
                      ? {
                          carouselCards: [
                            createEmptyCarouselCard(),
                            createEmptyCarouselCard(),
                          ],
                        }
                      : {}),
                  });
                  setStep(0);
                }}
              >
                <span className="wa-tpl-type-card-icon">
                  <Icon icon={type.icon} width={22} />
                </span>
                <strong>{type.label}</strong>
                <span>{type.description}</span>
              </button>
            ))}
          </div>

          <label className="wa-tpl-field">
            <span>Template Name *</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => updateForm({ name: e.target.value })}
              placeholder="e.g. order_confirmation"
            />
            <small>Only letters, numbers and underscores allowed</small>
          </label>

          <div className="wa-tpl-field-row">
            <label className="wa-tpl-field">
              <span>Language</span>
              <select
                value={form.language}
                onChange={(e) => updateForm({ language: e.target.value })}
              >
                {TEMPLATE_LANGUAGES.map((lang) => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="wa-tpl-field">
              <span>Category</span>
              <select
                value={form.category}
                onChange={(e) => updateForm({ category: e.target.value })}
              >
                {TEMPLATE_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <InfoBox>
            <li>Use lowercase letters only</li>
            <li>Use underscores instead of spaces (e.g., order_update)</li>
            <li>Maximum 512 characters allowed</li>
          </InfoBox>
        </>
      );
    }

    if (currentStepKey === "catalog") {
      return <TemplateCatalogStep form={form} updateForm={updateForm} />;
    }

    if (currentStepKey === "cards") {
      return <TemplateCarouselStep form={form} updateForm={updateForm} />;
    }

    if (currentStepKey === "header") {
      return (
        <>
          <div className="wa-tpl-radio-row">
            {[
              { value: "NONE", label: "None" },
              { value: "TEXT", label: "Text" },
              { value: "MEDIA", label: "Media" },
            ].map((opt) => (
              <label key={opt.value} className="wa-tpl-radio-pill">
                <input
                  type="radio"
                  name="headerFormat"
                  checked={form.headerFormat === opt.value}
                  onChange={() =>
                    updateForm({
                      headerFormat: opt.value,
                      headerText: "",
                      headerMediaFile: null,
                      headerMediaType:
                        opt.value === "MEDIA"
                          ? form.headerMediaType || "IMAGE"
                          : form.headerMediaType,
                    })
                  }
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>

          {form.headerFormat === "TEXT" ? (
            <label className="wa-tpl-field">
              <span>Header Text</span>
              <input
                type="text"
                maxLength={60}
                value={form.headerText}
                onChange={(e) => updateForm({ headerText: e.target.value })}
                placeholder="Enter header text"
              />
              <small>{form.headerText.length}/60</small>
            </label>
          ) : null}

          {form.headerFormat === "MEDIA" ? (
            <>
              <div className="wa-tpl-field">
                <span>Media type *</span>
                <div className="wa-tpl-radio-row compact">
                  {HEADER_MEDIA_TYPES.map((opt) => (
                    <label key={opt.value} className="wa-tpl-radio-pill">
                      <input
                        type="radio"
                        name="headerMediaType"
                        checked={form.headerMediaType === opt.value}
                        onChange={() =>
                          updateForm({
                            headerMediaType: opt.value,
                            headerMediaFile: null,
                          })
                        }
                      />
                      <span>
                        <Icon icon={opt.icon} width={14} /> {opt.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <label className="wa-tpl-field">
                <span>
                  Sample {getHeaderMediaTypeMeta(form.headerMediaType).label} *
                </span>
                <input
                  key={form.headerMediaType}
                  type="file"
                  accept={getHeaderMediaTypeMeta(form.headerMediaType).accept}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    if (!file) {
                      updateForm({ headerMediaFile: null });
                      return;
                    }
                    const check = validateHeaderMediaSample(
                      file,
                      form.headerMediaType || "IMAGE",
                    );
                    if (!check.ok) {
                      toast.error(check.error);
                      e.target.value = "";
                      updateForm({ headerMediaFile: null });
                      return;
                    }
                    updateForm({ headerMediaFile: file });
                  }}
                />
                {form.headerMediaFile ? (
                  <small className="wa-tpl-media-sample-ok">
                    <Icon icon="solar:check-circle-bold" width={14} />
                    Sample ready: {form.headerMediaFile.name}
                  </small>
                ) : (
                  <small>
                    {getHeaderMediaTypeMeta(form.headerMediaType).hint}
                  </small>
                )}
              </label>
            </>
          ) : null}

          <InfoBox>
            {form.headerFormat === "TEXT" ? (
              <li>Text header maximum 60 characters</li>
            ) : form.headerFormat === "MEDIA" ? (
              <>
                <li>Meta requires a sample file matching the selected type</li>
                <li>Images: JPEG or PNG, clear and high quality</li>
                <li>Videos: MP4 only, maximum 16MB</li>
                <li>Documents: PDF only</li>
              </>
            ) : (
              <li>No header — message starts with the body text</li>
            )}
          </InfoBox>
        </>
      );
    }

    if (currentStepKey === "body") {
      return (
        <>
          <div className="wa-tpl-field-head">
            <span>Message body *</span>
            <button type="button" className="wa-tpl-link-btn" onClick={handleAddVariable}>
              + Add Variable
            </button>
          </div>
          <textarea
            ref={bodyRef}
            className="wa-tpl-textarea"
            rows={6}
            maxLength={1024}
            value={form.bodyText}
            onChange={(e) => updateForm({ bodyText: e.target.value })}
            placeholder="Enter your message body..."
          />
          <small className="wa-tpl-counter">{form.bodyText.length}/1024</small>

          <label className="wa-tpl-field">
            <span>Footer (optional)</span>
            <input
              type="text"
              maxLength={60}
              value={form.footerText}
              onChange={(e) => updateForm({ footerText: e.target.value })}
              placeholder="Footer text"
            />
            <small>{form.footerText.length}/60</small>
          </label>

          <InfoBox>
            <li>Use clear and concise language</li>
            <li>Add dynamic variables like {"{{1}}"}, {"{{2}}"}</li>
            <li>Provide examples for all variables used</li>
            <li>Footer is optional but recommended for compliance</li>
          </InfoBox>
        </>
      );
    }

    if (currentStepKey === "buttons") {
      return (
        <>
          <div className="wa-tpl-radio-row">
            {[
              { value: "NONE", label: "None" },
              { value: "QUICK_REPLY", label: "Quick Reply" },
              { value: "CALL_TO_ACTION", label: "Call to Action" },
            ].map((opt) => (
              <label key={opt.value} className="wa-tpl-radio-pill">
                <input
                  type="radio"
                  name="buttonMode"
                  checked={form.buttonMode === opt.value}
                  onChange={() =>
                    updateForm({
                      buttonMode: opt.value,
                      quickReplyButtons: [],
                      ctaButtons: [],
                    })
                  }
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>

          {form.buttonMode === "QUICK_REPLY" ? (
            <div className="wa-tpl-buttons-section">
              <div className="wa-tpl-section-title">Quick Reply Buttons (Max 3)</div>
              {form.quickReplyButtons.map((btn, idx) => (
                <div key={`qr-${idx}`} className="wa-tpl-button-row">
                  <input
                    type="text"
                    maxLength={20}
                    placeholder="Button text..."
                    value={btn.text}
                    onChange={(e) => {
                      const next = [...form.quickReplyButtons];
                      next[idx] = { ...next[idx], text: e.target.value };
                      updateForm({ quickReplyButtons: next });
                    }}
                  />
                  <button
                    type="button"
                    className="wa-tpl-icon-btn"
                    onClick={() =>
                      updateForm({
                        quickReplyButtons: form.quickReplyButtons.filter(
                          (_, i) => i !== idx,
                        ),
                      })
                    }
                  >
                    <Icon icon="solar:close-circle-linear" width={18} />
                  </button>
                </div>
              ))}
              {form.quickReplyButtons.length < 3 ? (
                <button type="button" className="wa-tpl-add-btn" onClick={handleAddQuickReply}>
                  + Add Quick Reply
                </button>
              ) : null}
            </div>
          ) : null}

          {form.buttonMode === "CALL_TO_ACTION" ? (
            <div className="wa-tpl-buttons-section">
              <div className="wa-tpl-section-title">Call to Action Buttons (Max 2)</div>
              {form.ctaButtons.map((btn, idx) => (
                <div key={`cta-${idx}`} className="wa-tpl-cta-card">
                  <div className="wa-tpl-cta-card-head">
                    <span>{btn.type === "PHONE" ? "Call - Phone" : "Visit Website"}</span>
                    <button
                      type="button"
                      className="wa-tpl-icon-btn"
                      onClick={() =>
                        updateForm({
                          ctaButtons: form.ctaButtons.filter((_, i) => i !== idx),
                        })
                      }
                    >
                      <Icon icon="solar:close-circle-linear" width={18} />
                    </button>
                  </div>
                  <input
                    type="text"
                    maxLength={20}
                    placeholder="Button text..."
                    value={btn.text}
                    onChange={(e) => {
                      const next = [...form.ctaButtons];
                      next[idx] = { ...next[idx], text: e.target.value };
                      updateForm({ ctaButtons: next });
                    }}
                  />
                  {btn.type === "PHONE" ? (
                    <input
                      type="text"
                      placeholder="+1234567890"
                      value={btn.phone}
                      onChange={(e) => {
                        const next = [...form.ctaButtons];
                        next[idx] = { ...next[idx], phone: e.target.value };
                        updateForm({ ctaButtons: next });
                      }}
                    />
                  ) : (
                    <input
                      type="url"
                      placeholder="https://example.com"
                      value={btn.url}
                      onChange={(e) => {
                        const next = [...form.ctaButtons];
                        next[idx] = { ...next[idx], url: e.target.value };
                        updateForm({ ctaButtons: next });
                      }}
                    />
                  )}
                </div>
              ))}

              {form.ctaButtons.length < 2 ? (
                <div className="wa-tpl-cta-add-row">
                  <div className="wa-tpl-radio-row compact">
                    <label className="wa-tpl-radio-pill">
                      <input type="radio" name="ctaAddType" defaultChecked />
                      <span>Visit Website</span>
                    </label>
                    <label className="wa-tpl-radio-pill">
                      <input type="radio" name="ctaAddType" />
                      <span>Call Phone</span>
                    </label>
                  </div>
                  <div className="wa-tpl-cta-add-actions">
                    <button
                      type="button"
                      className="wa-tpl-add-btn"
                      onClick={() => handleAddCta("URL")}
                    >
                      Add Website
                    </button>
                    <button
                      type="button"
                      className="wa-tpl-add-btn"
                      onClick={() => handleAddCta("PHONE")}
                    >
                      Add Phone
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <InfoBox>
            <li>Maximum 3 quick reply buttons allowed</li>
            <li>Maximum 2 call-to-action buttons allowed</li>
            <li>Button text maximum 20 characters</li>
          </InfoBox>
        </>
      );
    }

    if (currentStepKey === "variables") {
      if (!variables.length) {
        return (
          <div className="wa-tpl-empty-step">
            <Icon icon="solar:check-circle-linear" width={40} />
            <p>No variables in your template. Continue to review.</p>
          </div>
        );
      }

      return (
        <>
          <p className="wa-tpl-step-desc">
            These examples help Meta review your template accurately
          </p>
          {variables.map((v) => {
            const value = form.variableExamples[v] || "";
            const invalid = !value.trim();
            return (
              <label key={v} className={`wa-tpl-field ${invalid ? "invalid" : ""}`}>
                <span className="wa-tpl-var-label">{`{{${v}}}`}</span>
                <input
                  type="text"
                  value={value}
                  placeholder={`Example for {{${v}}}...`}
                  onChange={(e) =>
                    updateForm({
                      variableExamples: {
                        ...form.variableExamples,
                        [v]: e.target.value,
                      },
                    })
                  }
                />
                {invalid ? (
                  <small className="wa-tpl-field-error">Example value is required</small>
                ) : null}
              </label>
            );
          })}

          <InfoBox>
            <li>Provide realistic and meaningful example values</li>
            <li>Examples are reviewed by Meta during approval</li>
            <li>All variables must have examples before submitting</li>
          </InfoBox>
        </>
      );
    }

    return (
      <>
        <InfoBox>
          <li>Review all template details carefully</li>
          <li>Ensure all variables have realistic examples</li>
          <li>Check that content complies with Meta&apos;s policies</li>
        </InfoBox>

        <div className="wa-tpl-review-card">
          <div className="wa-tpl-review-card-title">Template Details</div>
          <dl className="wa-tpl-review-dl">
            <div>
              <dt>Name</dt>
              <dd>{normalizeTemplateName(form.name) || "—"}</dd>
            </div>
            <div>
              <dt>Language</dt>
              <dd>
                {TEMPLATE_LANGUAGES.find((l) => l.value === form.language)?.label ||
                  form.language}
              </dd>
            </div>
            <div>
              <dt>Category</dt>
              <dd>
                {TEMPLATE_CATEGORIES.find((c) => c.value === form.category)?.label ||
                  form.category}
              </dd>
            </div>
            <div>
              <dt>Template Type</dt>
              <dd>
                <Icon
                  icon={
                    TEMPLATE_TYPES.find((t) => t.value === form.templateType)?.icon ||
                    "solar:document-text-linear"
                  }
                  width={16}
                />
                {TEMPLATE_TYPES.find((t) => t.value === form.templateType)?.label ||
                  "Standard"}
              </dd>
            </div>
          </dl>
        </div>

        <div className="wa-tpl-review-card">
          <div className="wa-tpl-review-card-title">Content</div>
          {form.templateType === "CATALOG" && form.catalogThumbnailFile ? (
            <div className="wa-tpl-review-block">
              <div className="wa-tpl-review-label">CATALOG THUMBNAIL</div>
              <div>{form.catalogThumbnailFile.name}</div>
            </div>
          ) : null}
          {form.templateType === "STANDARD" && form.headerFormat === "TEXT" && form.headerText ? (
            <div className="wa-tpl-review-block">
              <div className="wa-tpl-review-label">HEADER (OPTIONAL)</div>
              <div>{form.headerText}</div>
            </div>
          ) : null}
          {form.templateType === "STANDARD" && form.headerFormat === "MEDIA" ? (
            <div className="wa-tpl-review-block">
              <div className="wa-tpl-review-label">
                HEADER ({form.headerMediaType || "IMAGE"})
              </div>
              <div>{form.headerMediaFile?.name || "Sample file selected"}</div>
            </div>
          ) : null}
          <div className="wa-tpl-review-block">
            <div className="wa-tpl-review-label">MESSAGE BODY *</div>
            <div className="wa-tpl-review-body">
              {(form.bodyText || "—").split(/(\{\{\d+\}\})/g).map((part, idx) =>
                /^\{\{\d+\}\}$/.test(part) ? (
                  <span key={idx} className="wa-tpl-var-chip">
                    {part}
                  </span>
                ) : (
                  <span key={idx}>{part}</span>
                ),
              )}
            </div>
          </div>
          {form.footerText ? (
            <div className="wa-tpl-review-block">
              <div className="wa-tpl-review-label">FOOTER (OPTIONAL)</div>
              <div>{form.footerText}</div>
            </div>
          ) : null}
          {form.templateType === "CATALOG" ? (
            <div className="wa-tpl-review-block">
              <div className="wa-tpl-review-label">BUTTONS</div>
              <div className="wa-tpl-review-tags">
                <span className="wa-tpl-review-tag">
                  <Icon icon="solar:bag-4-linear" width={14} />
                  View Catalog
                </span>
              </div>
            </div>
          ) : null}
          {form.templateType === "CAROUSEL" ? (
            <div className="wa-tpl-review-block">
              <div className="wa-tpl-review-label">CARDS ({form.carouselCards?.length || 0})</div>
              <div className="wa-tpl-review-tags">
                {(form.carouselCards || []).map((card, i) => (
                  <span key={card.id} className="wa-tpl-review-tag">
                    Card {i + 1}: {card.bodyText || "—"}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {form.templateType === "STANDARD" && form.buttonMode !== "NONE" ? (
            <div className="wa-tpl-review-block">
              <div className="wa-tpl-review-label">BUTTONS</div>
              <div className="wa-tpl-review-tags">
                {form.buttonMode === "QUICK_REPLY"
                  ? form.quickReplyButtons
                      .filter((b) => b.text?.trim())
                      .map((b, i) => (
                        <span key={i} className="wa-tpl-review-tag">
                          {b.text}
                        </span>
                      ))
                  : form.ctaButtons
                      .filter((b) => b.text?.trim())
                      .map((b, i) => (
                        <span key={i} className="wa-tpl-review-tag">
                          <Icon
                            icon={
                              b.type === "PHONE"
                                ? "solar:phone-linear"
                                : "solar:link-round-linear"
                            }
                            width={14}
                          />
                          {b.type === "PHONE" ? "Call" : "Website"} · {b.text}
                        </span>
                      ))}
              </div>
            </div>
          ) : null}
        </div>

        {variables.length ? (
          <div className="wa-tpl-review-card">
            <div className="wa-tpl-review-card-title">Variables</div>
            <dl className="wa-tpl-review-dl">
              {variables.map((v) => (
                <div key={v}>
                  <dt>{`{{${v}}}`}</dt>
                  <dd>{form.variableExamples[v] || "—"}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <WhatsAppPageShell className="whatsapp-page-shell--panel">
      <div className="whatsapp-inbox-root">
      <div className="whatsapp-page-panel whatsapp-page-panel--wizard">
      <div className="wa-tpl-wizard-page">
        <div className="wa-tpl-wizard-header">
          <div className="wa-tpl-wizard-title-row">
            <div className="wa-tpl-wizard-icon">
              <Icon icon="solar:document-add-linear" width={22} />
            </div>
            <div>
              <h1>Create Meta Template</h1>
              <p>Build and submit WhatsApp message templates for your campaigns</p>
            </div>
          </div>
        </div>

        <div className="wa-tpl-stepper">
          {wizardSteps.map((item, idx) => {
            const done = idx < step;
            const active = idx === step;
            return (
              <div
                key={item.key}
                className={`wa-tpl-step ${done ? "done" : ""} ${active ? "active" : ""}`}
              >
                <div className="wa-tpl-step-circle">
                  {done ? (
                    <Icon icon="solar:check-read-linear" width={16} />
                  ) : (
                    idx + 1
                  )}
                </div>
                <span>{item.label}</span>
              </div>
            );
          })}
        </div>

        <div className={`wa-tpl-wizard-body ${showPreview ? "" : "no-preview"}`}>
          <div className="wa-tpl-wizard-form-card">
            {stepError ? <div className="wa-tpl-step-error">{stepError}</div> : null}
            {renderStepContent()}

            <div className="wa-tpl-wizard-footer">
              <button
                type="button"
                className="wa-tpl-btn-outline"
                onClick={step === 0 ? () => navigate("/whatsapp/templates") : goBack}
                disabled={submitting}
              >
                <Icon icon="solar:alt-arrow-left-linear" width={16} />
                {step === 0 ? "Cancel" : "Back"}
              </button>

              <div className="wa-tpl-wizard-footer-right">
                <button
                  type="button"
                  className="wa-tpl-btn-outline"
                  onClick={() => setShowPreview((v) => !v)}
                >
                  <Icon
                    icon={showPreview ? "solar:eye-closed-linear" : "solar:eye-linear"}
                    width={16}
                  />
                  {showPreview ? "Hide Preview" : "Show Preview"}
                </button>

                {step < wizardSteps.length - 1 ? (
                  <button type="button" className="wa-tpl-btn-primary" onClick={goNext}>
                    Next
                    <Icon icon="solar:alt-arrow-right-linear" width={16} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="wa-tpl-btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    <Icon icon="solar:plain-2-bold" width={16} />
                    {submitting ? "Submitting..." : "Save Changes"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {showPreview ? (
            <div className="wa-tpl-wizard-preview">
              <TemplatePhonePreview
                form={form}
                showExamples={isReviewStep || currentStepKey === "variables"}
              />
            </div>
          ) : null}
        </div>
      </div>
      </div>
      </div>
    </WhatsAppPageShell>
  );
}
